/**
 * One ParkourAI Doc, open for authoring.
 *
 * WHICH DOCUMENT, AND HOW IT IS NAMED IN THE URL. A revision holds as many of these as its author
 * needs, so the path carries both: `/document/:revisionUSID` means "the first part of this
 * revision" -- what somebody arriving from a link that predates several of them can sensibly mean --
 * and `/document/:revisionUSID/:documentUSID` names one exactly.
 *
 * `?create=1` CREATES ONE AND OPENS IT. That is QMS's Create button: the point of it is that
 * pressing it puts you in the editor, so the name is the server's next free "Document N" rather
 * than a dialog asking what to call something that has no words in it yet. Renaming is one click
 * once there is something to name.
 *
 * REACHED FROM QMS, NOT BROWSED TO. A document is created and governed there; this is where its
 * electronic representation is written.
 */
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";

import DocumentEditor from "../components/Docs/DocumentEditor";
import DocumentTitle from "../components/Docs/DocumentTitle";
import DocumentParts from "../components/Docs/DocumentParts";
import TemplateDialog from "../components/Docs/TemplateDialog";
import { useMqttClient } from "../util/MqttClient";
import { makeCresAxios } from "../util/CresAxios";
import { accept, withBody, writeBody } from "../util/bodyTransport";
import { uploadFigure } from "../components/Docs/figureUpload";
import { replayAt } from "../components/Docs/updateLog";
import useAppContext from "../util/AppContext";

export default function DocumentPage() {

    // The session comes from AppContext, as every other page here takes it -- a page that took it
    // as a prop would be the only one, and the route would have to thread it through.
    const sessionContext = useAppContext()?.sessionContext as any;
    const { revisionUSID, documentUSID } = useParams();
    const [search, setSearch] = useSearchParams();
    const navigate = useNavigate();
    const mqtt = useMqttClient(sessionContext);

    const [resolved, setResolved] = React.useState<{
        usid: string; name: string; editable: boolean; record: any; status: string | null;
        /** Whether this document is one of the shells the gallery offers. */
        isTemplate?: boolean;
    } | null>(null);
    const [problem, setProblem] = React.useState<string | null>(null);
    // "Create from existing document", arrived at from QMS as ?from=1. The picker lives here
    // rather than in QMS because every write to a document goes through this tier -- QMS choosing
    // what this then copies would put half the decision on the other side of that line.
    const [sourcesOpen, setSourcesOpen] = React.useState(false);
    const [sources, setSources] = React.useState<any[]>([]);
    const [sourcesLoading, setSourcesLoading] = React.useState(false);
    const [sourceQuery, setSourceQuery] = React.useState("");
    // Bumped when a rename or a create lands, so the strip of parts reflects it without a reload.
    const [changed, setChanged] = React.useState(0);
    // File > Delete asks; DocumentParts confirms and performs — see the prop's note for why the
    // dialog is not duplicated onto the menu.
    const [deleteRequest, setDeleteRequest] = React.useState(0);
    const [partCount, setPartCount] = React.useState(0);
    const [savedAt, setSavedAt] = React.useState<number | null>(null);

    /**
     * Is there a session?
     *
     * NOTHING IS DRAWN WITHOUT ONE, and on this page that is a disclosure question rather than a
     * tidiness one. Ed saw a document's text at this URL before logging in. The page had no guard
     * at all: it mounted the editor unconditionally, so whatever the editor was holding -- a render
     * restored from the back/forward cache, state left over from a session that had just expired --
     * was on screen with no session behind it. The server was never fooled; the browser simply kept
     * painting.
     *
     * A controlled document's text is the thing this product exists to control, so the page now
     * refuses to render rather than relying on a fetch failing.
     */
    const signedIn = !!sessionContext?.valid?.();

    const axios = React.useMemo(() => makeCresAxios(sessionContext), [sessionContext]);

    const api = React.useMemo(() => ({
        // THE BODY MAY NOT ARRIVE AS `Body`. A lambda carries 6MB each way, so a large document comes
        // back gzipped or as a presigned S3 link, and `withBody` resolves whichever arrived back into
        // `.Body` before anything downstream sees it. `accept()` is what tells the server this browser
        // can read those -- a browser without CompressionStream asks for nothing and gets the plain
        // body it has always got. See src/util/bodyTransport.ts.
        get: async (usid: string) =>
            withBody((await axios.get(
                `/t11/v1/document_content?USID=${encodeURIComponent(usid)}`
                + `&Accept=${encodeURIComponent(accept())}`)).data),
        // ADDRESSED BY REVISION, WHICH IS A DIFFERENT PARAMETER -- and sending one in the other's
        // place is not a near miss. USID means "this part"; RevisionUSID means "whatever part this
        // revision has, if any". Asking for a revision under USID finds no part, and the service
        // then has no revision to ask QMS about: every document with nothing authored on it
        // answered 500 instead of "nothing here yet", which is most of the list.
        getByRevision: async (revisionUSID: string) =>
            withBody((await axios.get(
                `/t11/v1/document_content?RevisionUSID=${encodeURIComponent(revisionUSID)}`
                + `&Accept=${encodeURIComponent(accept())}`)).data),
        save: async (usid: string, body: any, expectedDigest: string | null,
                     acknowledgeLoss?: boolean, source?: string) =>
            (await axios.post(`/t11/v1/document_content`, {
                USID: usid,
                // Body, BodyGzip or BodyKey -- whichever this document's size calls for. The slot is
                // only ever asked for when the gzipped body still will not fit through the lambda.
                ...(await writeBody(body, async () =>
                    (await axios.get(
                        `/t11/v1/document_body_upload?USID=${encodeURIComponent(usid)}`)).data)),
                ExpectedDigest: expectedDigest,
                // AUTHORED unless the editor says otherwise. RESTORE is the only other value the
                // browser sends, and it makes the audit row read "Restored an earlier version"
                // -- and makes the service keep a snapshot, which it does for every structural
                // event and not for an ordinary save.
                ...(source ? { Source: source } : {}),
                // NEVER DEFAULTED TRUE. It is only ever set because a person was shown what the
                // save would remove and said yes.
                AcknowledgeLoss: !!acknowledgeLoss,
            })).data,
        // THE RENDITION: the PDF this document is signed as, built by the server because a
        // browser cannot produce a running header or a page number. It answers with a presigned
        // S3 link rather than the file -- a download never comes through the API.
        rendition: async (usid: string) =>
            (await axios.get(`/t11/v1/document_rendition?USID=${encodeURIComponent(usid)}`)).data,
        // THE WORD COPY, by the same route: built on the server, stored, and the browser sent to a
        // presigned address. See downloads-never-through-lambda.
        templates: async () =>
            (await axios.get("/t11/v1/document_contents?Templates=true")).data,
        // THE SAME READ WITHOUT THE TEMPLATE FLAG: every document this reader can see, for
        // "Create from existing document". Scoped away from this revision's own parts by the
        // server, so the picker cannot offer a document to copy onto itself.
        sources: async (query: string) =>
            (await axios.get("/t11/v1/document_contents", {
                params: { Sources: "true", Search: query || undefined,
                          RevisionUSID: revisionUSID },
            })).data,
        // THE APPROVED VOCABULARY, asked for on the document_content route rather than one of its
        // own -- see the handler for why. Asked for separately from the body because the glossary
        // is the same for every part of every document and the body is not.
        glossary: async (revisionUSID: string) =>
            (await axios.get(`/t11/v1/document_content?Glossary=1`
                + `&RevisionUSID=${encodeURIComponent(revisionUSID)}`)).data,
        wordExport: async (usid: string) =>
            (await axios.get(`/t11/v1/document_export?USID=${encodeURIComponent(usid)}`)).data,
        // A FIGURE GOES STRAIGHT TO S3 and never through the API -- a 6MB lambda limit means a
        // photograph could not come through it, and one that could would still be billed as if it
        // had. See src/components/Docs/figureUpload.ts.
        uploadFigure: (usid: string, file: File) => uploadFigure(axios, usid, file),
        // THE OPERATION LOG. Every edit is kept, so the history can show any moment rather than
        // only the ones a five-minute snapshot caught -- which is how Google Docs has always done
        // it, and we were already producing the stream for collaboration. See updateLog.ts.
        // THE CONVERSATIONS. Gated on seeing the document rather than editing it -- a revision out
        // for approval is exactly when somebody wants to ask about it.
        comments: async (usid: string) =>
            (await axios.get(`/t11/v1/document_comment?USID=${encodeURIComponent(usid)}`)).data,
        createComment: async (usid: string, body: string, anchorText: string, mentions?: string[]) =>
            (await axios.post(`/t11/v1/document_comment`,
                              { USID: usid, Body: body, AnchorText: anchorText,
                                Mentions: mentions })).data,
        replyToComment: async (commentUSID: string, body: string, mentions?: string[]) =>
            (await axios.post(`/t11/v1/document_comment`,
                              { USID: commentUSID, Action: "reply", Body: body,
                                Mentions: mentions })).data,
        // WHO MAY BE NAMED. On the comments route rather than one of its own -- it is a read about
        // this document's conversation as much as the threads are.
        mentionablePeople: async (usid: string, search: string) =>
            ((await axios.get(`/t11/v1/document_comment?USID=${encodeURIComponent(usid)}`
                              + `&People=1&Search=${encodeURIComponent(search)}`)).data
             ?.People ?? []),
        resolveComment: async (commentUSID: string, resolved: boolean) =>
            (await axios.post(`/t11/v1/document_comment`,
                              { USID: commentUSID, Action: "resolve", Resolved: resolved })).data,
        recordUpdates: async (usid: string, updates: string[], checkpoint?: string) =>
            (await axios.post(`/t11/v1/document_updates`,
                              { USID: usid, Updates: updates, Checkpoint: checkpoint })).data,
        replayAt: (usid: string, at?: string) => replayAt(axios, usid, at),
        // COPY A VERSION INTO A NEW PART, which is how somebody drafts the next revision from an
        // old one. No new service action: creating a part has always accepted a body, and the
        // version's body is one the caller already has in hand.
        copyToNewPart: async (name: string, body: any) => {
            const made = (await axios.post(`/t11/v1/document_contents`,
                                           { RevisionUSID: revisionUSID, Name: name,
                                             Body: body })).data;
            // THE ADDRESS BECOMES THE NEW PART'S, pushed rather than replaced: Back should return
            // to the version you copied FROM, which is where the reader was.
            navigate(`/document/${revisionUSID}/${made.USID}`);
            return made;
        },
        // Fresh display URLs, for an editing session that outlives the hour a presigned one lasts.
        //
        // NAMES THE DOCUMENT, and that is a check rather than a convenience: a bare list of
        // attachment ids would make this a way to turn any id into a URL, so the server asks the
        // persona about THIS document and returns only figures that belong to it.
        figureUrls: async (usid: string, attachments: string[]) =>
            (await axios.get(`/t11/v1/document_image?USID=${encodeURIComponent(usid)}`
                             + "&AttachmentUSIDs=" + encodeURIComponent(attachments.join(",")))).data,
        // PUT, because an import REPLACES this part's text rather than adding to it. A second
        // import of the same file is the same document, not two.
        importDocx: async (usid: string, base64: string, filename: string) =>
            (await axios.put(`/t11/v1/document_content`, {
                USID: usid, Docx: base64, Filename: filename,
            })).data,
    }), [axios]);

    // RESOLVED ONCE PER DOCUMENT, not once per mount: switching parts changes the address without
    // remounting this page, and a resolve that ran only on mount would leave the editor on the part
    // somebody just navigated away from.
    //
    // KEYED ON THE ADDRESS, not a bare "already ran" flag, because creating REPLACES the address
    // with the new document's own -- and re-running on that change would create a second one.
    const started = React.useRef<string | null>(null);

    React.useEffect(() => {
        // Nothing is resolved, fetched or created without a session either -- see `signedIn`.
        if (!signedIn) return;
        const key = `${revisionUSID}/${documentUSID ?? ""}/${search.get("create") ?? ""}`;
        if (started.current === key) return;
        started.current = key;
        setResolved(null);

        (async () => {
            try {
                if (documentUSID) {
                    const got = await api.get(documentUSID);
                    setResolved({ usid: documentUSID, name: got?.Name ?? "",
                                  editable: !!got?.Editable, record: got?.Record ?? null,
                                  status: got?.Status ?? null,
                                  isTemplate: !!got?.IsTemplate });
                    return;
                }

                if (search.get("create") === "1") {
                    const made = (await axios.post("/t11/v1/document_contents",
                                                   { RevisionUSID: revisionUSID })).data;
                    // THE ADDRESS BECOMES THE DOCUMENT'S OWN, replacing rather than pushing: Back
                    // should leave the editor, not create another document.
                    navigate(`/document/${revisionUSID}/${made.USID}`, { replace: true });
                    // The create response does not carry the record; the editor's own load fills
                    // it in a moment later. Showing a blank line briefly beats a second round trip.
                    setResolved({ usid: made.USID, name: made.Name, editable: true, record: null,
                                  status: null });
                    return;
                }

                const got = await api.getByRevision(revisionUSID!);
                if (got?.Exists) {
                    setResolved({ usid: got.USID, name: got.Name ?? "", editable: !!got.Editable,
                                  record: got.Record ?? null, status: got.Status ?? null });
                } else {
                    // NOTHING WRITTEN YET IS NOT AN ERROR. It is the normal state of every record
                    // before somebody chooses to manage its text here.
                    setProblem("Nothing has been authored on this revision yet.");
                }
            } catch (error: any) {
                setProblem(error?.response?.data?.Cause || error?.response?.data?.Code
                           || "This document could not be opened.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID, documentUSID, signedIn]);

    // ARRIVED TO CHOOSE A SOURCE. Opened from the address; the flag is cleared below alongside
    // `create`, for the same reason -- a reload should not reopen a dialog somebody dismissed.
    React.useEffect(() => {
        if (search.get("from") === "1") setSourcesOpen(true);
    }, [search]);

    React.useEffect(() => {
        if (!sourcesOpen) return;
        let cancelled = false;
        setSourcesLoading(true);
        // Debounced: the search runs across every document in the tenant, and a query per
        // keystroke would ask the database to do that work five times for one word.
        const timer = setTimeout(() => {
            api.sources(sourceQuery)
                .then((answer: any) => { if (!cancelled) setSources(answer?.Templates ?? []); })
                .catch(() => { if (!cancelled) setSources([]); })
                .finally(() => { if (!cancelled) setSourcesLoading(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourcesOpen, sourceQuery]);

    React.useEffect(() => {
        // The create flag has done its work; leaving it in the address would make a reload create
        // another document.
        if (search.get("create")) {
            const next = new URLSearchParams(search);
            next.delete("create");
            next.delete("from");
            setSearch(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved]);

    // BEFORE EVERYTHING, including the "no document named" case: a signed-out visitor gets the
    // same nothing whatever the address says, so the page cannot be used to probe which documents
    // exist.
    if (!signedIn) return null;

    if (!revisionUSID) {
        return <Typography sx={{ p: 3 }}>No document named.</Typography>;
    }

    if (problem) {
        return <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}><Alert severity="info">{problem}</Alert></Box>;
    }

    if (!resolved) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}><CircularProgress/></Box>;
    }

    return (
        // FULL WIDTH, AS GOOGLE DOCS IS. The chrome -- heading, menus, toolbar -- runs the width
        // of the window and the PAGE is centred in the canvas beneath it. Constraining the whole
        // editor to 1100px and centring that instead made the toolbar a floating island and left
        // the canvas ending in mid-air, which is the one thing a document surface must not do:
        // the grey has to reach the edges or it stops reading as a desk the paper is lying on.
        <Box sx={{ px: 3, py: 2 }}>
            <DocumentTitle documentUSID={resolved.usid} name={resolved.name}
                           revisionUSID={revisionUSID} record={resolved.record}
                           status={resolved.status} savedAt={savedAt}
                           onRenamed={(name) => { setResolved({ ...resolved, name });
                                                  setChanged(n => n + 1); }}/>
            <DocumentParts revisionUSID={revisionUSID} documentUSID={resolved.usid}
                           editable={resolved.editable} reloadKey={changed}
                           deleteRequest={deleteRequest} onCount={setPartCount}
                           onChanged={() => setChanged(n => n + 1)}/>
            <DocumentEditor
                documentUSID={resolved.usid}
                revisionUSID={revisionUSID}
                onNewDocument={async () => {
                    const made = (await axios.post("/t11/v1/document_contents",
                                                   { RevisionUSID: revisionUSID })).data;
                    navigate(`/document/${revisionUSID}/${made.USID}`);
                    setChanged(n => n + 1);
                }}
                // ONE CALL, NOT CREATE-THEN-SAVE. The body comes from the template server-side, so
                // there is no moment where the new document exists and is empty -- which is the
                // moment a second window, an autosave or a lost connection would make permanent.
                onNewFromExisting={() => setSourcesOpen(true)}
                onNewFromTemplate={async (templateUSID: string) => {
                    const made = (await axios.post("/t11/v1/document_contents",
                                                   { RevisionUSID: revisionUSID,
                                                     FromTemplateUSID: templateUSID })).data;
                    navigate(`/document/${revisionUSID}/${made.USID}`);
                    setChanged(n => n + 1);
                }}
                isTemplate={resolved.isTemplate}
                onSetTemplate={async (next: boolean) => {
                    await axios.put("/t11/v1/document_contents",
                                    { USID: resolved.usid, IsTemplate: next });
                    setResolved(one => (one ? { ...one, isTemplate: next } : one));
                }}
                onRename={() => {
                    const field = document.querySelector<HTMLInputElement>('input[aria-label="Document name"]');
                    field?.focus();
                    field?.select();
                }}
                onImported={() => setChanged(n => n + 1)}
                onSaved={setSavedAt}
                record={resolved.record} status={resolved.status}
                onOpenList={() => navigate("/")}
                onDuplicate={async () => {
                    // MADE FROM THE SAVED BODY, not the editor's -- a copy taken mid-keystroke
                    // would be a document nobody wrote. The create then save is two calls because
                    // creating names the part and saving carries the text; one call that did both
                    // would be a second write path into DOCUMENT_CONTENT.
                    const current = await axios.get(
                        `/t11/v1/document_content?USID=${encodeURIComponent(resolved.usid)}`);
                    const made = (await axios.post("/t11/v1/document_contents",
                                                   { RevisionUSID: revisionUSID })).data;
                    await axios.post("/t11/v1/document_content",
                                     { USID: made.USID, Body: current.data?.Body ?? {} });
                    navigate(`/document/${revisionUSID}/${made.USID}`);
                    setChanged(n => n + 1);
                }}
                onDelete={resolved.editable && partCount > 1
                    ? () => setDeleteRequest(n => n + 1) : undefined}
                deleteUnavailable={!resolved.editable
                    ? "This revision is not open for editing."
                    : partCount > 1 ? undefined
                    : "A revision keeps at least one document. Delete the revision in QMS instead."}
                mqttClient={mqtt.client}
                sessionContext={sessionContext}
                api={api}
            />

            {/* THE PICKER SITS HERE, NOT IN THE EDITOR. The editor is one document; this asks
                about every document in the tenant, and mounting it inside would make a component
                scoped to one revision the owner of a cross-revision read. */}
            <TemplateDialog
                open={sourcesOpen}
                templates={sources}
                loading={sourcesLoading}
                search={sourceQuery}
                onSearch={setSourceQuery}
                title="Create from existing document"
                empty={sourceQuery.trim()
                    ? <>No document matches “{sourceQuery.trim()}”.</>
                    : <>There is nothing here you can start from yet. Documents appear once they
                       exist on a record your persona can see.</>}
                note={<>
                    The text and figures are copied as they are now. Later changes to the original
                    will not appear here, and none of its history, comments or suggestions come
                    across.
                </>}
                onClose={() => setSourcesOpen(false)}
                onStart={async (source) => {
                    setSourcesOpen(false);
                    try {
                        const made = (await axios.post("/t11/v1/document_contents",
                                                       { RevisionUSID: revisionUSID,
                                                         FromDocumentUSID: source.USID })).data;
                        navigate(`/document/${revisionUSID}/${made.USID}`);
                        setChanged(n => n + 1);
                    } catch (error: any) {
                        // SAID, NOT SWALLOWED. The duplicate-name refusal is the likely one and it
                        // is actionable -- the reader renames and tries again -- so the server's
                        // own sentence is better than a dialog that simply closes.
                        setProblem(error?.response?.data?.Cause || error?.response?.data?.Code
                                   || "That document could not be copied onto this revision.");
                    }
                }}/>
        </Box>
    );
}
